import { Box, Skeleton, Stack, Typography } from '@mui/material'
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import { QAModePlayerControls } from './QAModePlayerControls'
import { MAX_VOLUME } from '../constants'
import { RRWebPlayer, RRWebPlayerProxy } from '../rrweb/RRWebPlayer'
import { VideoEmptyState } from '../rrweb/VideoEmptyState'
import { Scrubber } from './Scrubber'
import { DEAD_SPACE_MAX_SECONDS, getActivityTypeDisplayName } from './scrubberUtils'
import { StackProps } from '@mui/material/Stack'
import { ProcessedActivity } from '../../../../api/models'
import { useLocationCameras } from '../../../../api/apiQueries'
import { ReplayerEvents } from '@rrweb/types'
import { ProcessedRRWebEvents } from '../rrweb/types'
import { RRWebPlugin } from '../rrweb/plugin/RRWebPlugin'
import { SpecialistAudio, SpecialistAudioProxy } from './SpecialistAudio'
import { ValidatedRecording } from '../ertUtils'
import { Banner, BannerType } from '../../../../components/Banner/Banner'
import { useVideoEmptyState } from '../useVideoEmptyState'
import { namedLogger, LoggerName } from '../../../../utils/logger'

const logger = namedLogger(LoggerName.ErtPlayer)

/**
 * Interface for the ErtPlayer ref object
 */
export interface ErtPlayerRef {
  seek: (timeInSeconds: number) => void
}

/**
 * Props for the `Replayer` component.
 */
type ErtPlayerProps = StackProps & {
  locationId: string
  engagementId: string
  processedActivities: ProcessedActivity[]
  onCurrentTimeChange?: (currentTimeInSeconds: number) => void
  recordingStartTimestamp: number | undefined
  onIsPlayingChange?: (isPlaying: boolean) => void
  webRecordings: ValidatedRecording[]
  audioRecordings: ValidatedRecording[]
  processedRRWebEvents: ProcessedRRWebEvents
}

type RefHolder<T> = {
  key: string
  ref: T
}

const loadingTextContainerStyles = {
  position: 'absolute',
  top: '40%',
  right: 0,
  left: 0,
  alignItems: 'center',
  justifyContent: 'center',
  pointerEvents: 'none'
}

const loadingTextStyles = {
  p: 'var(--spacing-3xl)',
  color: 'white',
  fontSize: 'var(--size-xl)',
  backgroundColor: 'var(--neutral-800-t80)',
  borderRadius: 'var(--border-radius-l)'
}

const videoContainerStyles = {
  position: 'relative',
  flex: '1 1 0',
  width: '100%'
}

const rrwebPlayerStyles = {
  boxSizing: 'content-box',
  width: '100%',
  height: '100%',
  backgroundColor: 'var(--bg-surface)',
  borderRadius: 'var(--border-radius-l)',
  overflow: 'hidden'
}

const noRecordingBannerStyles = {
  marginY: 'var(--spacing-l)'
}

const videoSkeletonStyles = {
  transform: 'unset',
  width: '100%',
  flex: '1 1 0',
  borderRadius: 'var(--border-radius-l)'
}

const scrubberSkeletonStyles = {
  transform: 'unset',
  margin: '.5rem 1rem',
  width: '100%',
  height: '.75rem',
  borderRadius: 'var(--radius-m)'
}

const controlsSkeletonStyles = {
  transform: 'unset',
  margin: '.5rem 1rem',
  width: '35rem',
  height: '3rem',
  borderRadius: '1.5rem'
}

/**
 * RRWeb replayer with a custom user interface. It expects a sequence of RRWeb events as input.
 */
export const ErtPlayer = forwardRef<ErtPlayerRef, ErtPlayerProps>(
  (
    {
      locationId,
      engagementId,
      processedActivities,
      onCurrentTimeChange,
      recordingStartTimestamp,
      onIsPlayingChange,
      webRecordings,
      audioRecordings,
      processedRRWebEvents,
      sx,
      ...stackProps
    },
    ref
  ) => {
    const [pluginReady, setPluginReady] = useState<boolean>(false)

    const [rrwebPlugin, setRrwebPlugin] = useState<RRWebPlugin>()

    const useLocationCamerasQuery = useLocationCameras(locationId)
    const cameras = useLocationCamerasQuery.data

    const camerasById = useMemo(() => {
      const map = new Map()
      cameras?.forEach(camera => {
        map.set(camera.uuid, camera)
      })
      return map
    }, [cameras])

    useEffect(() => {
      // process the events and add end times to the video-start events
      if (engagementId && locationId && useLocationCamerasQuery.isSuccess) {
        const asyncWork = async () => {
          try {
            setPluginReady(false)
            const rrWebPlugin = new RRWebPlugin(processedRRWebEvents, camerasById)
            setRrwebPlugin(rrWebPlugin)

            // we can't call getErtPlugin() on the next line until the plugin is fully initialized or the next line will blow up
            await rrWebPlugin.waitForInitializationToComplete()

            // this calls getErtPlugin() for the first time. This is only safe because we awaited initialization above
            rrWebPlugin.getErtPlugin().onReady(() => {
              setPluginReady(true)
            })
          } catch (e) {
            logger.error('Failed to initialize RRWeb plugin', e)
          }
        }
        asyncWork()
      }
    }, [camerasById, engagementId, locationId, useLocationCamerasQuery.isSuccess, processedRRWebEvents])

    const [currentTimeInSeconds, setCurrentTimeInSeconds] = useState(0)
    const currentTimeInSecondsRef = useRef(currentTimeInSeconds)
    currentTimeInSecondsRef.current = currentTimeInSeconds

    const [hasStartedPlaying, setHasStartedPlaying] = useState(false)

    const { labeledActivity, activeDeadSpaceRange } = useVideoEmptyState(processedRRWebEvents, processedActivities, currentTimeInSeconds)

    const [durationInSeconds, setDurationInSeconds] = useState(0)
    const [speed, setSpeed] = useState(1)
    const [isPlaying, updateIsPlaying] = useState(false)
    const setIsPlaying = useCallback(
      (playing: boolean) => {
        updateIsPlaying(playing)
        onIsPlayingChange?.(playing)
      },
      [onIsPlayingChange]
    )
    const isPlayingRef = useRef(isPlaying)
    isPlayingRef.current = isPlaying
    const [isWaiting, setIsWaiting] = useState<boolean>(false)
    // Tracks an in-flight seek so the buffering overlay can render before rrweb's synchronous goto() blocks the thread.
    const [isSeeking, setIsSeeking] = useState<boolean>(false)
    const wasPlayingBeforeBufferingStarted = useRef(false)

    const rrwebPlayerRef = useRef<RRWebPlayerProxy>(null)
    const specialistAudioProxies = useRef<RefHolder<SpecialistAudioProxy>[]>([])

    const doForAllAudioProxies = useCallback((action: (ref: SpecialistAudioProxy) => void) => {
      for (const specialistAudioRefHolder of specialistAudioProxies.current) {
        action(specialistAudioRefHolder.ref)
      }
    }, [])

    const handleSeek = useCallback(
      (seekToSecond: number) => {
        if (!rrwebPlayerRef.current) return

        // Show the overlay flag and move the scrubber ball state to the
        // target time so it lands at the dragged position before rrweb's goto() blocks the thread.
        setIsSeeking(true)
        setCurrentTimeInSeconds(seekToSecond)
        setHasStartedPlaying(true)
        onCurrentTimeChange?.(seekToSecond)

        // requestAnimationFrame always run right before the next repaint.
        // If we just had 1, the `rrwebPlayerRef.current?.goto` would block the thread before the `setIsSeeking(true)`
        // update reflects the loading state in the UI. So nesting a 2nd one ensures that the loading overlay gets a
        // chance to render before the seek is processed
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            logger.debug('handleSeek start')
            try {
              // The rrweb player does not provide a way to intercept a seek event, so we have to notify the plugin ourselves
              rrwebPlugin?.getErtPlugin().beforeSeek(seekToSecond * 1000)

              logger.debug('handleSeek calling goto', seekToSecond)

              // this must get called between rrwebPlugin.beforeSeek() and rrwebPlugin.afterSeek() to ensure the plugin can handle the seek event correctly.
              rrwebPlayerRef.current?.goto(seekToSecond * 1000)
              doForAllAudioProxies(audioProxy => audioProxy.currentTimeChanged(seekToSecond))
            } catch (error) {
              logger.error('Error seeking to second', error as Error)
            } finally {
              // The rrweb player does not provide a way to intercept a seek event, so we have to notify the plugin ourselves before and after the seek is processed
              rrwebPlugin?.getErtPlugin().afterSeek(Math.max(0, seekToSecond) * 1000)
              setIsSeeking(false)
              logger.debug('handleSeek end')
            }
          })
        })
      },
      [rrwebPlugin, doForAllAudioProxies, onCurrentTimeChange]
    )

    // Once playback (or a manual seek) has spent DEAD_SPACE_MAX_SECONDS inside one,
    // jump straight to where video actually resumes.
    const handleDeadSpaceJump = useCallback(
      (currentTimeInSeconds: number) => {
        if (!activeDeadSpaceRange) return

        const elapsedInRangeSeconds = currentTimeInSeconds - activeDeadSpaceRange.relativeStartTimeSeconds
        if (elapsedInRangeSeconds >= DEAD_SPACE_MAX_SECONDS && currentTimeInSeconds < activeDeadSpaceRange.relativeEndTimeSeconds) {
          handleSeek(activeDeadSpaceRange.relativeEndTimeSeconds)
        }
      },
      [activeDeadSpaceRange, handleSeek]
    )

    useEffect(() => {
      if (pluginReady) {
        // listen for time changes and update the scrubber
        rrwebPlayerRef.current?.onCurrentTimeChange((currentTimeInSeconds: number) => {
          setCurrentTimeInSeconds(currentTimeInSeconds)
          rrwebPlugin?.getErtPlugin().saveCurrentTime(currentTimeInSeconds)
          onCurrentTimeChange?.(currentTimeInSeconds)
          doForAllAudioProxies(audioProxy => audioProxy.currentTimeChanged(currentTimeInSeconds))
          handleDeadSpaceJump(currentTimeInSeconds)
        })
        rrwebPlayerRef.current?.onDurationSeconds((durationSeconds: number) => {
          setDurationInSeconds(durationSeconds)
        })
        rrwebPlayerRef.current?.addEventListener(ReplayerEvents.Pause, () => {
          setIsPlaying(false)
          doForAllAudioProxies(audioProxy => audioProxy.pause())
        })
        rrwebPlayerRef.current?.addEventListener(ReplayerEvents.Resume, () => {
          setIsPlaying(true)
          setHasStartedPlaying(true)
          doForAllAudioProxies(audioProxy => audioProxy.play(currentTimeInSecondsRef.current))
        })
        rrwebPlayerRef.current?.addEventListener(ReplayerEvents.Start, () => {
          setIsPlaying(true)
          setHasStartedPlaying(true)
          doForAllAudioProxies(audioProxy => audioProxy.play(currentTimeInSecondsRef.current))
        })
        rrwebPlayerRef.current?.addEventListener(ReplayerEvents.Finish, () => {
          setIsPlaying(false)
          doForAllAudioProxies(audioProxy => audioProxy.pause())
        })
        rrwebPlugin?.getErtPlugin().onBufferingStart(wasPlaying => {
          setIsWaiting(true)
          wasPlayingBeforeBufferingStarted.current = wasPlaying
          doForAllAudioProxies(audioProxy => audioProxy.pause())
        })
        rrwebPlugin?.getErtPlugin().onBufferingEnd(() => {
          setIsWaiting(false)
          if (wasPlayingBeforeBufferingStarted.current) {
            doForAllAudioProxies(audioProxy => audioProxy.play(currentTimeInSecondsRef.current))
          }
          wasPlayingBeforeBufferingStarted.current = false
        })
      }
    }, [rrwebPlugin, pluginReady, onCurrentTimeChange, doForAllAudioProxies, setIsPlaying, handleDeadSpaceJump])

    const onPlay = useCallback(() => {
      rrwebPlayerRef.current?.play()
      doForAllAudioProxies(audioProxy => audioProxy.play(currentTimeInSecondsRef.current))
    }, [doForAllAudioProxies])

    const onPause = useCallback(() => {
      rrwebPlayerRef.current?.pause()
      doForAllAudioProxies(audioProxy => audioProxy.pause())
    }, [doForAllAudioProxies])

    const onSpeedChange = useCallback(
      (speedMultiplier: number) => {
        rrwebPlayerRef.current?.setSpeed(speedMultiplier)
        rrwebPlugin?.getErtPlugin().handleSpeedChange(speedMultiplier)
        setSpeed(speedMultiplier)
        doForAllAudioProxies(audioProxy => audioProxy.setSpeed(speedMultiplier))
      },
      [rrwebPlugin, doForAllAudioProxies]
    )

    const onSpecialistAudioVolumePercentageChange = useCallback(
      (fraction: number) => {
        doForAllAudioProxies(audioProxy => audioProxy.setVolume(fraction))
      },
      [doForAllAudioProxies]
    )

    const onPlayerVolumePercentageChange = useCallback(
      (fraction: number) => {
        rrwebPlugin?.getErtPlugin().handleVolumePercentageChange(fraction)
      },
      [rrwebPlugin]
    )

    // Expose the seek function through the ref
    useImperativeHandle(
      ref,
      () => ({
        seek: handleSeek
      }),
      [handleSeek]
    )

    const isReady = rrwebPlugin && pluginReady

    const containerStyles = useMemo<StackProps['sx']>(
      () => ({
        alignItems: 'center',
        gap: 'var(--spacing-3xs)',
        position: 'relative',
        borderRadius: 'var(--border-radius-l)',
        ...sx
      }),
      [sx]
    )

    return (
      <Stack sx={containerStyles} {...stackProps}>
        {rrwebPlugin && rrwebPlugin.getProcessedEvents().events.length > 0 ? (
          <>
            <Box sx={videoContainerStyles}>
              <RRWebPlayer
                key={rrwebPlugin.getInstanceNumber()} // this is here to force a full remount of the player when plugin is recreated (happens during HMR in dev)
                ref={rrwebPlayerRef}
                processedRRWebEvents={rrwebPlugin.getProcessedEvents()}
                rrwebPlugin={rrwebPlugin}
                autoPlay={isPlaying}
                sx={rrwebPlayerStyles}
              />
              {hasStartedPlaying && activeDeadSpaceRange && labeledActivity && (
                <VideoEmptyState
                  activityTypeDisplayName={getActivityTypeDisplayName(labeledActivity.type)}
                  shortenedSegmentDurationSeconds={Math.min(labeledActivity.durationSeconds, DEAD_SPACE_MAX_SECONDS)}
                  segmentDurationSeconds={labeledActivity.durationSeconds}
                />
              )}
            </Box>
            {audioRecordings?.map(audioRecording => (
              <SpecialistAudio
                key={audioRecording.recordingId}
                ref={ref => {
                  if (ref) {
                    specialistAudioProxies.current.push({ key: audioRecording.recordingId, ref })
                  } else {
                    specialistAudioProxies.current.findIndex(entry => entry.key === audioRecording.recordingId)
                    specialistAudioProxies.current = specialistAudioProxies.current.filter(entry => entry.key !== audioRecording.recordingId)
                  }
                }}
                audioRecording={audioRecording}
              />
            ))}
            <Scrubber
              currentTimeInSeconds={currentTimeInSeconds}
              recordingStartTimestamp={recordingStartTimestamp}
              processedActivities={processedActivities}
              durationInSeconds={durationInSeconds}
              onSeek={handleSeek}
              lineHeight="0"
            />
            <QAModePlayerControls
              currentTimeInSeconds={currentTimeInSeconds}
              durationInSeconds={durationInSeconds}
              isPlaying={isPlaying}
              onPlay={onPlay}
              isBuffering={isWaiting || isSeeking || !isReady}
              onPause={onPause}
              speed={speed}
              onSpeedChange={onSpeedChange}
              initialVolumePercentage={MAX_VOLUME / 100}
              onPlayerVolumePercentageChange={onPlayerVolumePercentageChange}
              onSpecialistAudioVolumePercentageChange={onSpecialistAudioVolumePercentageChange}
              webRecordings={webRecordings}
              audioRecordings={audioRecordings}
              engagementId={engagementId}
            />
            {(isWaiting || isSeeking || !isReady) && (
              <Stack sx={loadingTextContainerStyles}>
                <Typography sx={loadingTextStyles}>Loading...</Typography>
              </Stack>
            )}
          </>
        ) : rrwebPlugin && rrwebPlugin.getProcessedEvents().events.length === 0 ? (
          <Banner type={BannerType.Error} title={'No Recording Found'} sx={noRecordingBannerStyles} data-testid="no-recording-found-banner" />
        ) : (
          <>
            <Skeleton sx={videoSkeletonStyles} />
            <Skeleton sx={scrubberSkeletonStyles} />
            <Skeleton sx={controlsSkeletonStyles} />
          </>
        )}
      </Stack>
    )
  }
)
